import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { Modal } from "react-bootstrap";

import "moment-timezone";

import {
  setLoading as setFeedbacksLoading,
  setFeedbacks,
  setError,
} from "../../features/common/feedbackSlice";
import { deleteFeedbackById, deletePackageById, feedbackList } from "../../services/userservice";

const Feedbacks = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const { feedbacks, loading, error } = useSelector(
    (state) => state?.feedback
  );
  const [showModal, setShowModal] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [deleteUser, setDeleteUser] = useState("");
  const dispatch = useDispatch();

  useEffect(() => {
    const fetchFeedbackListData = async () => {
      try {
        dispatch(setFeedbacksLoading());
        const response = await feedbackList();
        dispatch(setFeedbacks(response));
      } catch (error) {
        dispatch(setError(error.message));
      }
    };
    fetchFeedbackListData();
  }, [dispatch, deleteUser]);

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  // Filter feedbacks based on search term
  const filteredFeedbacks = (feedbacks || []).filter(
    (feedback) =>
      feedback?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      feedback?.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      feedback?.content?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      feedback?.holiday_destination?.name
        ?.toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      feedback?.packageCategories?.some((cat) =>
        cat?.name?.toLowerCase().includes(searchTerm.toLowerCase())
      )
  );

  if (loading) return <div>Loading...</div>;
  if (error) return <div>Error: {error}</div>;

  const handleDelete = async () => {
    try {
      const deleteResponse = await deleteFeedbackById(selectedUserId);
      if (deleteResponse.status === 200 || deleteResponse.status === 201) {
        setDeleteUser(selectedUserId);
        closeModal();
      } else {
        setError(deleteResponse.message || "Failed to delete ");
      }
    } catch (error) {
      alert("You are not authorized to delete this user");
    }
  };

  const openModal = (userId) => {
    setSelectedUserId(userId);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
  };

  const confirmDelete = async () => {
    await handleDelete(selectedUserId);
    closeModal();
  };

  // Pagination
  const indexOfLastFeedback = currentPage * rowsPerPage;
  const indexOfFirstFeedback = indexOfLastFeedback - rowsPerPage;
  const feedbackLists = filteredFeedbacks.slice(
    indexOfFirstFeedback,
    indexOfLastFeedback
  );

  const totalPages = Math.ceil(filteredFeedbacks.length / rowsPerPage);

  const renderPageNumbers = () => {
    const pages = [];
    if (totalPages <= 10) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1, 2, 3);
      if (currentPage > 5) pages.push("...");
      const start = Math.max(4, currentPage - 1);
      const end = Math.min(totalPages - 3, currentPage + 1);
      for (let i = start; i <= end; i++) {
        if (!pages.includes(i)) pages.push(i);
      }
      if (currentPage < totalPages - 4) pages.push("...");
      pages.push(totalPages - 2, totalPages - 1, totalPages);
    }

    return pages.map((page, index) => (
      <li
        key={index}
        className={`page-item ${
          page === currentPage ? "active" : ""
        } ${page === "..." ? "disabled" : ""}`}
      >
        {page === "..." ? (
          <span className="page-link">...</span>
        ) : (
          <button className="page-link" onClick={() => setCurrentPage(page)}>
            {page}
          </button>
        )}
      </li>
    ));
  };

  return (
    <div className="content-wrapper">
      <div className="content-header">
        <div className="container-fluid">
          <div className="row mb-2">
            <div className="col-sm-6">
              <h1 className="m-0">Feedbacks</h1>
            </div>
            <div className="col-sm-6">
              <ol className="breadcrumb float-sm-right">
                <li className="breadcrumb-item">
                  <Link to="/dashboard">Home</Link>
                </li>
                <li className="breadcrumb-item active">Feedbacks</li>
              </ol>
            </div>
          </div>
        </div>
      </div>

      <section className="content">
        <div className="container-fluid">
          <div className="row">
            <div className="col-md-12">
              <div className="card">
                {/* Header */}
                <div className="card-header d-md-flex align-items-center justify-content-end">
                  <Link
                    to="/add-feedback"
                    className="btn btn-primary mb-md-0 mb-2 py-1 mr-3"
                  >
                    <i className="fas fa-plus" /> Add Feedback
                  </Link>
                  <div className="card-tools">
                    <div
                      className="input-group input-group-sm"
                      style={{ width: "250px" }}
                    >
                      <input
                        type="text"
                        name="table_search"
                        className="form-control float-right"
                        placeholder="Search"
                        value={searchTerm}
                        onChange={handleSearchChange}
                      />
                      <div className="input-group-append">
                        <button type="submit" className="btn btn-default">
                          <i className="fas fa-search" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Body */}
                <div className="card-body">
                  {/* Table */}
                  <div className="table-responsive">
                    <table className="table table-bordered table-hover">
                      <thead>
                        <tr>
                          <th>ID</th>
                          <th>Name</th>
                          <th>Category Type</th>
                          <th>Type</th>
                          <th>Rating</th>
                          <th>Destination</th>
                          <th>Categories</th>
                          <th>Status</th>
                          <th style={{ width: "15%" }}>Action</th>
                        </tr>
                      </thead>
                      {feedbackLists.length > 0 ? (
                        <tbody>
                          {feedbackLists.map((feedback) => (
                            <tr key={feedback.id}>
                              <td>{feedback.id}</td>
                              <td>{feedback.name}</td>
                              <td>{feedback.categoryType}</td>
                              <td>{feedback.feedbackType}</td>
                              <td>{feedback.rating}</td>
                              <td>
                                {feedback.holiday_destination?.name || "N/A"}
                              </td>
                              <td>
                                {feedback.packageCategories?.length > 0 ? (
                                  <ul>
                                    {feedback.packageCategories.map((cat) => (
                                      <li key={cat.id}>{cat.name}</li>
                                    ))}
                                  </ul>
                                ) : (
                                  <span>No Categories</span>
                                )}
                              </td>
                              <td>
                                <span
                                  className={`badge p-2 w-100 ${
                                    feedback?.status ===
                                    "active"
                                      ? "badge-success"
                                      : "badge-danger"
                                  }`}
                                >
                                  {feedback?.status?.toUpperCase() ||
                                    "N/A"}
                                </span>
                              </td>
                              <td>
                                <div
                                  className="d-flex align-items-center"
                                  style={{ gap: "10px" }}
                                >
                                  <Link to={`/edit-feedback/${feedback.id}`}>
                                    <button className="btn btn-primary btn-sm">
                                      Edit
                                    </button>
                                  </Link>

                                  <button
                                    className="btn btn-danger btn-sm"
                                    onClick={() => openModal(feedback?.id)}
                                  >
                                    Delete
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      ) : (
                        <tbody>
                          <tr>
                            <td colSpan="9" className="text-center">
                              No Feedbacks found
                            </td>
                          </tr>
                        </tbody>
                      )}
                    </table>
                  </div>

                  {/* Pagination */}
                  <div className="d-md-flex justify-content-between align-items-center mt-3">
                    <div>
                      Showing {indexOfFirstFeedback + 1} to{" "}
                      {Math.min(indexOfLastFeedback, filteredFeedbacks.length)} of{" "}
                      {filteredFeedbacks.length} entries
                    </div>
                    <nav>
                      <ul className="pagination mb-0">
                        <li
                          className={`page-item ${
                            currentPage === 1 ? "disabled" : ""
                          }`}
                        >
                          <button
                            className="page-link"
                            onClick={() => setCurrentPage(currentPage - 1)}
                          >
                            Previous
                          </button>
                        </li>

                        {renderPageNumbers()}

                        <li
                          className={`page-item ${
                            currentPage === totalPages ? "disabled" : ""
                          }`}
                        >
                          <button
                            className="page-link"
                            onClick={() => setCurrentPage(currentPage + 1)}
                          >
                            Next
                          </button>
                        </li>
                      </ul>
                    </nav>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Delete Modal */}
      <Modal show={showModal} onHide={closeModal}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="text-center p-4">
            <h5>Are you sure you want to delete this Feedback?</h5>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button className="btn btn-secondary" onClick={closeModal}>
            Cancel
          </button>
          <button className="btn btn-danger" onClick={confirmDelete}>
            OK
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Feedbacks;
